// @ts-nocheck
import { AppProps } from 'next/app';
import Head from 'next/head';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { DebugModeBadge } from '../components/DebugModeBadge';
import {
    DuplicateEmailError,
    AuthenticationError,
    ApiError,
} from '../types/errors';
import { suppressExpectedErrors } from '../utils/errorSuppression';
import { ToastProvider } from '../contexts/ToastContext';
import { CampaignProvider } from '../contexts/CampaignContext';
import { UserProvider } from '../contexts/UserContext';
import { GenresProvider } from '../contexts/GenresContext';
import { GoogleAuthProvider } from '../providers/GoogleAuthProvider';
import { PostHogProvider } from '../providers/PostHogProvider';
import { billingApi } from '../services/billingApi';
import logger from '../utils/logger';
import '../styles/globals.css';

function MyApp({ Component, pageProps }: AppProps) {
    // Only log for non-health-check requests (client-side only)
    if (typeof window !== 'undefined') {
        logger.debug('_app.tsx: Initializing MyApp component');
        logger.debug(
            '_app.tsx: pageProps:',
            JSON.stringify(pageProps, null, 2)
        );
    }

    // Global error handlers and initialization
    useEffect(() => {
        logger.debug('_app.tsx: Setting up global error handlers');

        // Apply error suppression for development
        suppressExpectedErrors();

        // Pre-fetch billing plans in the background
        // This warms the cache so the billing modal opens instantly
        billingApi.prefetchPlans();

        // Monkey patch DOM methods to prevent Google Translate from crashing React
        // This handles cases where Google Translate wraps text in <font> tags,
        // causing React reconciliation errors during component updates
        if (typeof Node === 'function' && Node.prototype) {
            const originalRemoveChild = Node.prototype.removeChild;
            Node.prototype.removeChild = function (child) {
                if (child.parentNode !== this) {
                    logger.debug(
                        '[DOM Patch] removeChild: node is not a child of this parent, ignoring'
                    );
                    return child;
                }
                return originalRemoveChild.apply(this, arguments);
            };

            const originalInsertBefore = Node.prototype.insertBefore;
            Node.prototype.insertBefore = function (newNode, referenceNode) {
                if (referenceNode && referenceNode.parentNode !== this) {
                    logger.debug(
                        '[DOM Patch] insertBefore: referenceNode is not a child of this parent, ignoring'
                    );
                    return newNode;
                }
                return originalInsertBefore.apply(this, arguments);
            };
        }

        const handleError = (event: ErrorEvent) => {
            // Check if this is a MetaMask/Web3 error
            if (
                event.message &&
                (event.message.includes('MetaMask') ||
                    event.message.includes('eth_accounts') ||
                    event.message.includes('eth_requestAccounts') ||
                    event.message.includes('Failed to connect to MetaMask') ||
                    event.message.includes('The method does not exist'))
            ) {
                logger.debug(
                    'Global Error Handler: Suppressing MetaMask error:',
                    event.message
                );
                event.preventDefault();
                return;
            }

            // Check if this is a PostHog analytics error
            if (
                event.message &&
                (event.message.includes('posthog') ||
                    event.message.includes('PostHog') ||
                    event.message.includes('Failed to fetch') ||
                    event.message.includes('ERR_BLOCKED_BY_CLIENT'))
            ) {
                logger.debug(
                    'Global Error Handler: Suppressing PostHog error:',
                    event.message
                );
                event.preventDefault();
                return;
            }

            // Check if this is an expected error
            if (
                event.error instanceof DuplicateEmailError ||
                event.error instanceof AuthenticationError ||
                (event.error instanceof ApiError &&
                    event.error.errorCode === 'DUPLICATE_EMAIL')
            ) {
                logger.debug(
                    'Global Error Handler: Suppressing expected error:',
                    event.error.message
                );
                event.preventDefault(); // Prevent the default error handling (Next.js overlay)
                return;
            }

            logger.error('Global Error', event.error, {
                message: event.message,
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
            });
        };

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            // Check if this is a MetaMask/Web3 error
            if (
                event.reason &&
                (event.reason.message?.includes('MetaMask') ||
                    event.reason.message?.includes('eth_accounts') ||
                    event.reason.message?.includes('eth_requestAccounts') ||
                    event.reason.message?.includes(
                        'Failed to connect to MetaMask'
                    ) ||
                    event.reason.message?.includes(
                        'The method does not exist'
                    ) ||
                    event.reason.code === -32601 || // JSON-RPC error code for method not found
                    event.reason.code === 4001) // User rejected request
            ) {
                logger.debug(
                    'Unhandled Rejection: Suppressing MetaMask error:',
                    event.reason.message || event.reason
                );
                event.preventDefault();
                return;
            }

            // Check if this is a PostHog error
            if (
                event.reason &&
                (event.reason.message?.includes('posthog') ||
                    event.reason.message?.includes('PostHog') ||
                    event.reason.message?.includes('Failed to fetch') ||
                    event.reason.message?.includes('ERR_BLOCKED_BY_CLIENT') ||
                    event.reason.message?.includes('/decide') ||
                    event.reason.message?.includes('/e?ip=1'))
            ) {
                logger.debug(
                    'Unhandled Rejection: Suppressing PostHog error:',
                    event.reason.message || event.reason
                );
                event.preventDefault();
                return;
            }

            // Check if this is an expected error
            if (
                event.reason instanceof DuplicateEmailError ||
                event.reason instanceof AuthenticationError ||
                (event.reason instanceof ApiError &&
                    event.reason.errorCode === 'DUPLICATE_EMAIL')
            ) {
                logger.debug(
                    'Unhandled Rejection: Suppressing expected error:',
                    event.reason.message
                );
                event.preventDefault(); // Prevent the default error handling (Next.js overlay)
                return;
            }

            logger.error('Unhandled Promise Rejection', event.reason);
        };

        window.addEventListener('error', handleError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        return () => {
            logger.debug('_app.tsx: Cleaning up global error handlers');
            window.removeEventListener('error', handleError);
            window.removeEventListener(
                'unhandledrejection',
                handleUnhandledRejection
            );
        };
    }, []);

    const [queryClient] = useState(() => {
        if (typeof window !== 'undefined') {
            logger.debug('_app.tsx: Creating QueryClient');
        }
        return new QueryClient({
            defaultOptions: {
                queries: {
                    retry: 2,
                    staleTime: 5 * 60 * 1000, // 5 minutes
                    throwOnError: false, // Don't throw errors to error boundaries
                },
                mutations: {
                    retry: 1,
                    throwOnError: false, // Don't throw errors to error boundaries
                },
            },
        });
    });

    if (typeof window !== 'undefined') {
        logger.debug(
            '_app.tsx: Rendering component:',
            Component.name || 'Unknown'
        );
    }

    return (
        <ErrorBoundary>
            <Head>
                {/* Mobile viewport configuration for proper scaling */}
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
                />
            </Head>
            <QueryClientProvider client={queryClient}>
                <PostHogProvider>
                    <UserProvider>
                        <GoogleAuthProvider>
                            <GenresProvider>
                                <CampaignProvider>
                                    <ToastProvider>
                                        {/* No background here - let individual pages handle their own backgrounds */}
                                        <Component {...pageProps} />
                                        {/* Debug mode badge - only visible when ?debug=true */}
                                        <DebugModeBadge />
                                    </ToastProvider>
                                </CampaignProvider>
                            </GenresProvider>
                        </GoogleAuthProvider>
                    </UserProvider>
                </PostHogProvider>
            </QueryClientProvider>
        </ErrorBoundary>
    );
}

export default MyApp;
